const MARKET_SELECTOR_CHOICE_KEY = "theme:market-selector-choice";
const MARKET_SELECTOR_DEBUG_KEY = "theme:market-selector-debug";

const safeStorage = {
  get(key) {
    try {
      return window.localStorage.getItem(key);
    } catch (error) {
      return null;
    }
  },
  set(key, value) {
    try {
      window.localStorage.setItem(key, value);
    } catch (error) {}
  },
  remove(key) {
    try {
      window.localStorage.removeItem(key);
    } catch (error) {}
  },
};

function getStoredChoice() {
  const raw = safeStorage.get(MARKET_SELECTOR_CHOICE_KEY);

  if (!raw) {
    return null;
  }

  try {
    return JSON.parse(raw);
  } catch (error) {
    return null;
  }
}

function setStoredChoice(choice) {
  safeStorage.set(MARKET_SELECTOR_CHOICE_KEY, JSON.stringify(choice));
}

function clearStoredChoice() {
  safeStorage.remove(MARKET_SELECTOR_CHOICE_KEY);
}

function normalizeCode(value) {
  return String(value || "")
    .trim()
    .toLowerCase();
}

function buildSuggestionsUrls(rootUrl) {
  const normalizedRoot =
    rootUrl && rootUrl !== "/" ? `${rootUrl.replace(/\/$/, "")}/` : "/";

  return Array.from(
    new Set([
      `${normalizedRoot}browsing_context_suggestions.json`,
      "/browsing_context_suggestions.json",
    ]),
  );
}

function getNestedValue(source, paths) {
  for (const path of paths) {
    const value = path
      .split(".")
      .reduce((accumulator, key) => accumulator?.[key], source);

    if (value) {
      return value;
    }
  }

  return "";
}

function getSuggestionPart(suggestion, part) {
  return getNestedValue(suggestion, [
    `parts.${part}.handle`,
    `parts.${part}.iso_code`,
    `parts.${part}.code`,
    `${part}.handle`,
    `${part}.iso_code`,
    `${part}.code`,
    part,
  ]);
}

function getSuggestedCountry(data) {
  const suggestion = Array.isArray(data?.suggestions)
    ? data.suggestions[0]
    : data?.suggestion;

  return (
    getSuggestionPart(suggestion, "country") ||
    getNestedValue(data, [
      "detected_values.country.handle",
      "detected_values.country.iso_code",
      "detected_values.country.code",
      "detected_values.country",
      "detected.country.handle",
      "detected.country.iso_code",
      "detected.country.code",
      "country.handle",
      "country.iso_code",
      "country.code",
      "country",
    ])
  );
}

function getSuggestedLocale(data) {
  const suggestion = Array.isArray(data?.suggestions)
    ? data.suggestions[0]
    : data?.suggestion;

  return (
    getSuggestionPart(suggestion, "language") ||
    getSuggestionPart(suggestion, "locale") ||
    getNestedValue(data, [
      "detected_values.language.handle",
      "detected_values.language.iso_code",
      "detected_values.language.code",
      "detected_values.language",
      "detected_values.locale.handle",
      "detected_values.locale.iso_code",
      "detected_values.locale.code",
      "detected_values.locale",
      "detected.language.handle",
      "detected.language.iso_code",
      "detected.language.code",
      "detected.locale.handle",
      "detected.locale.iso_code",
      "detected.locale.code",
      "language.handle",
      "language.iso_code",
      "language.code",
      "language",
      "locale.handle",
      "locale.iso_code",
      "locale.code",
      "locale",
    ])
  );
}

function optionExists(select, value) {
  return Boolean(
    select &&
    Array.from(select.options).some(
      (option) => normalizeCode(option.value) === normalizeCode(value),
    ),
  );
}

function parseJsonScript(modal, selector, fallback) {
  const script = modal.querySelector(selector);

  if (!script) {
    return fallback;
  }

  try {
    return JSON.parse(script.textContent);
  } catch (error) {
    return fallback;
  }
}

function getLanguageConfig(modal) {
  const languages = parseJsonScript(
    modal,
    "[data-market-selector-language-options]",
    [],
  );
  const countryLanguages = parseJsonScript(
    modal,
    "[data-market-selector-country-languages]",
    {},
  );

  return { languages, countryLanguages };
}

function getLanguageOptionsForCountry(modal, countryCode) {
  const { languages, countryLanguages } = getLanguageConfig(modal);
  const languageCodes =
    countryLanguages[String(countryCode || "").toUpperCase()] ||
    countryLanguages.default ||
    [];
  const options = languageCodes
    .map((languageCode) =>
      languages.find(
        (language) =>
          normalizeCode(language.isoCode) === normalizeCode(languageCode),
      ),
    )
    .filter(Boolean);

  return options.length > 0 ? options : languages;
}

function getAvailableLanguagesForCountry(countrySelect, countryCode) {
  const countryOption = Array.from(countrySelect?.options || []).find(
    (option) => normalizeCode(option.value) === normalizeCode(countryCode),
  );

  if (!countryOption?.dataset.availableLanguages) {
    return [];
  }

  return countryOption.dataset.availableLanguages
    .split(",")
    .map((entry) => entry.split(":")[0]);
}

function rebuildLocaleSelect(modal, preferredLocale = "") {
  const countrySelect = modal.querySelector("[data-market-selector-country]");
  const localeSelect = modal.querySelector("[data-market-selector-locale]");

  if (!countrySelect || !localeSelect) {
    return;
  }

  const previousLocale = preferredLocale || localeSelect.value;
  const selectedCountryOption =
    countrySelect.options[countrySelect.selectedIndex];
  const languageOptions = selectedCountryOption?.dataset.availableLanguages
    ? selectedCountryOption.dataset.availableLanguages
        .split(",")
        .map((lang) => {
          const [isoCode, name] = lang.split(":");
          return { isoCode, name };
        })
    : [];

  localeSelect.innerHTML = "";

  languageOptions.forEach((language) => {
    localeSelect.options.add(new Option(language.name, language.isoCode));
  });

  if (!selectOption(localeSelect, previousLocale)) {
    localeSelect.selectedIndex = 0;
    localeSelect.dispatchEvent(new Event("change", { bubbles: true }));
  }
}

function selectOption(select, value) {
  if (!optionExists(select, value)) {
    return false;
  }

  select.value = Array.from(select.options).find(
    (option) => normalizeCode(option.value) === normalizeCode(value),
  ).value;
  select.dispatchEvent(new Event("change", { bubbles: true }));

  return true;
}

function updateFlag(modal) {
  const countrySelect = modal.querySelector("[data-market-selector-country]");
  const flag = modal.querySelector("[data-market-selector-flag]");

  if (!countrySelect || !flag) {
    return;
  }

  const selectedOption = countrySelect.options[countrySelect.selectedIndex];

  if (selectedOption?.dataset.flagSrc) {
    flag.src = selectedOption.dataset.flagSrc;
    flag.alt =
      selectedOption.dataset.flagAlt || selectedOption.textContent.trim();
  }
}

// Opens the selector with a market preselected instead of navigating straight
// to it. Used by the footer flags: they used to link directly to the market,
// which bypassed the selector entirely, so the visitor never confirmed the
// language and the choice was never remembered.
function openMarketSelectorForCountry(country, locale) {
  const modal = document.getElementById("market-selector-modal");
  const countrySelect = modal?.querySelector("[data-market-selector-country]");

  if (!modal || !countrySelect || !selectOption(countrySelect, country)) {
    return false;
  }

  // selectOption fires a change event, which rebuilds the locale select for
  // the new country - rebuild it again so an explicitly requested language
  // wins over that country's first one.
  rebuildLocaleSelect(modal, locale);
  updateFlag(modal);
  modal.show();

  return true;
}

// Any element carrying data-market-selector-country-link opens the selector
// for that country (and, optionally, data-market-selector-locale). Delegated
// from the document so markup rendered later still works.
function registerMarketSelectorLinks() {
  document.addEventListener("click", (event) => {
    const trigger = event.target?.closest?.(
      "[data-market-selector-country-link]",
    );

    if (!trigger) {
      return;
    }

    const opened = openMarketSelectorForCountry(
      trigger.dataset.marketSelectorCountryLink,
      trigger.dataset.marketSelectorLocale,
    );

    // When that market isn't in the selector, leave the link alone so it
    // still navigates the way it always did.
    if (opened) {
      event.preventDefault();
    }
  });
}

function setDebugState(state) {
  window.yayaMarketSelector = {
    ...state,
    clearSeen() {
      clearStoredChoice();
      window.location.reload();
    },
    show() {
      document.getElementById("market-selector-modal")?.show();
    },
    showForCountry(country, locale) {
      return openMarketSelectorForCountry(country, locale);
    },
  };

  if (safeStorage.get(MARKET_SELECTOR_DEBUG_KEY) === "true") {
    console.info("[YAYA market selector]", state);
  }
}

async function fetchSuggestions(urls) {
  let lastStatus = 0;

  for (const url of urls) {
    try {
      const response = await fetch(url, { credentials: "same-origin" });
      lastStatus = response.status;

      if (response.ok) {
        return { data: await response.json(), url, status: response.status };
      }
    } catch (error) {
      return { error, url, status: lastStatus };
    }
  }

  return { status: lastStatus, url: urls[urls.length - 1] };
}

// Whether the visitor submitted the localization form during this page view.
// Shared between the submit listener (registered for every page view, see
// rememberSubmittedChoice) and the "dismissed the modal" listener that is only
// registered when the modal is auto-shown.
let choiceSubmitted = false;

// The visitor can open the modal at any time from the header, not just when it
// is auto-shown - so the choice has to be remembered on every submit, no matter
// how the modal was opened. Registering this inside the auto-show flow meant a
// visitor who already had a remembered market (that flow returns early) could
// pick a new one without it being stored, and would then be auto-redirected
// back to the old market on the next page view.
function rememberSubmittedChoice(modal) {
  const form = modal.querySelector("form");

  if (!form) {
    return;
  }

  form.addEventListener("submit", () => {
    choiceSubmitted = true;

    // Read the form fields rather than the <select> elements: when the shop
    // only has one country or language the selector is a hidden input instead.
    setStoredChoice({
      status: "selected",
      country: form.elements.country_code?.value,
      locale: form.elements.locale_code?.value,
    });
  });
}

function redirectToMarket(modal, country, locale) {
  const countrySelect = modal.querySelector("[data-market-selector-country]");
  const localeSelect = modal.querySelector("[data-market-selector-locale]");
  const form = modal.querySelector("form");

  if (!form || !countrySelect || !localeSelect) {
    return false;
  }

  selectOption(countrySelect, country);
  rebuildLocaleSelect(modal, locale);
  selectOption(localeSelect, locale);

  if (typeof form.requestSubmit === "function") {
    form.requestSubmit();
  } else {
    form.submit();
  }

  return true;
}

async function showMarketSelectorWhenNeeded() {
  const isDesignMode = Boolean(window.Shopify?.designMode);
  const modal = document.getElementById("market-selector-modal");

  if (!modal) {
    setDebugState({ reason: "missing-modal" });
    return;
  }

  const countrySelect = modal.querySelector("[data-market-selector-country]");
  const localeSelect = modal.querySelector("[data-market-selector-locale]");
  const currentCountry = normalizeCode(modal.dataset.currentCountry);
  const currentLocale = normalizeCode(modal.dataset.currentLocale);
  const storedChoice = isDesignMode ? null : getStoredChoice();

  if (storedChoice?.status === "declined") {
    setDebugState({ reason: "declined-previously", storedChoice });
    return;
  }

  if (storedChoice?.status === "selected") {
    const storedCountry = normalizeCode(storedChoice.country);
    const storedLocale = normalizeCode(storedChoice.locale);
    const alreadyOnRememberedMarket =
      storedCountry === currentCountry && storedLocale === currentLocale;

    if (alreadyOnRememberedMarket) {
      setDebugState({ reason: "already-on-remembered-market", storedChoice });
      return;
    }

    // The locale <select> is a dependent dropdown - it only ever contains
    // the languages of whichever country is currently selected, so it can't
    // be used to check locale availability for a *different* stored
    // country. Check the target country's own available languages instead
    // (each country <option> carries its full language list in a data
    // attribute, regardless of which country is currently selected).
    const countryIsAvailable = countrySelect
      ? optionExists(countrySelect, storedCountry)
      : storedCountry === currentCountry;
    const localeIsAvailable = countrySelect
      ? getAvailableLanguagesForCountry(countrySelect, storedCountry).some(
          (locale) => normalizeCode(locale) === storedLocale,
        )
      : optionExists(localeSelect, storedLocale);

    if (countryIsAvailable && localeIsAvailable) {
      redirectToMarket(modal, storedChoice.country, storedChoice.locale);
      setDebugState({
        reason: "auto-redirected-to-remembered-market",
        storedChoice,
      });
      return;
    }

    // The remembered market is no longer available (e.g. it was removed) -
    // forget it and fall back to the normal suggestion-based flow.
    clearStoredChoice();
  }

  const suggestionsUrls = buildSuggestionsUrls(modal.dataset.rootUrl);
  const suggestionsResponse = await fetchSuggestions(suggestionsUrls);

  if (suggestionsResponse.error) {
    setDebugState({
      reason: "suggestions-request-error",
      error: suggestionsResponse.error,
      suggestionsUrl: suggestionsResponse.url,
      suggestionsUrls,
    });
    return;
  }

  if (!suggestionsResponse.data) {
    setDebugState({
      reason: "suggestions-request-failed",
      status: suggestionsResponse.status,
      suggestionsUrl: suggestionsResponse.url,
      suggestionsUrls,
    });
    return;
  }

  const suggestions = suggestionsResponse.data;
  const suggestedCountry = getSuggestedCountry(suggestions);
  const suggestedLocale = getSuggestedLocale(suggestions);
  const hasDifferentCountry =
    suggestedCountry &&
    normalizeCode(suggestedCountry) !== currentCountry &&
    optionExists(countrySelect, suggestedCountry);
  const hasDifferentLocale =
    suggestedLocale &&
    normalizeCode(suggestedLocale) !== currentLocale &&
    optionExists(localeSelect, suggestedLocale);

  if (!hasDifferentCountry && !hasDifferentLocale) {
    setDebugState({
      reason: "no-supported-mismatch",
      currentCountry,
      currentLocale,
      suggestedCountry,
      suggestedLocale,
      hasSuggestedCountryOption: optionExists(countrySelect, suggestedCountry),
      hasSuggestedLocaleOption: optionExists(localeSelect, suggestedLocale),
      suggestions,
    });
    // return;
  }

  selectOption(countrySelect, suggestedCountry);
  rebuildLocaleSelect(modal, suggestedLocale || currentLocale);
  selectOption(localeSelect, suggestedLocale);
  updateFlag(modal);

  if (!isDesignMode) {
    // Any other way of closing the modal (close button, Escape key, ...)
    // counts as the visitor dismissing it - remember that so it isn't
    // shown again.
    modal.addEventListener(
      "dialog:after-hide",
      () => {
        if (!choiceSubmitted) {
          setStoredChoice({ status: "declined" });
        }
      },
      { once: true },
    );
  }

  await customElements.whenDefined("x-modal");
  setDebugState({
    reason: "shown",
    currentCountry,
    currentLocale,
    suggestedCountry,
    suggestedLocale,
    hasDifferentCountry,
    hasDifferentLocale,
    isDesignMode,
    suggestionsUrl: suggestionsResponse.url,
    suggestions,
  });
  modal.show();
}

document.addEventListener("DOMContentLoaded", () => {
  const modal = document.getElementById("market-selector-modal");
  const countrySelect = modal?.querySelector("[data-market-selector-country]");

  countrySelect?.addEventListener("change", () => {
    rebuildLocaleSelect(modal);
    updateFlag(modal);
  });

  if (modal && countrySelect) {
    rebuildLocaleSelect(modal);
    modal.hideForOutsideClickTarget = () => false;
    modal.allowOutsideClickForTarget = () => true;
  }

  if (modal && !window.Shopify?.designMode) {
    rememberSubmittedChoice(modal);
  }

  registerMarketSelectorLinks();

  showMarketSelectorWhenNeeded();
});
